<template>
  <section class="sankey-diagram statistics">
    <Breadcrumbs
      :breadcrumbs="[
        { title: $t('menu.statistics'), disabled: true },
        { title: $t('menu.sankeydiagram'), disabled: true }
      ]">
    </Breadcrumbs>

    <form class="evollu-form">

      <div class="row" v-if="clusters.length">
        <div class="col-12 col-sm-5">
          <va-switch v-model="filters.showEntityStats">
            <span slot="trueTitle">{{$t('kiosks.entity')}}</span>
            <span slot="falseTitle">
              <span>
                {{$t('statistics.cluster')}}
              </span>
              <span v-if="!filters.showEntityStats && filters.cluster" @click.prevent="expandCluster = !expandCluster" class="cluster-dropdown">
                <i class="fa" :class="{'fa-chevron-up': expandCluster, 'fa-chevron-down': !expandCluster}"></i>
              </span>
            </span>
          </va-switch>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-sm-5">
          <span v-if="filters.showEntityStats">
            <label class="form-label">{{$t('statistics.select_entity')}}:</label>
            <multiselect
              id="entities-multiselect"
              v-model="filters.entity"
              :options="entityOptions"
              track-by="id"
              :custom-label="formatedEntityLabel"
              :placeholder="!entityOptions.length ? $t('statistics.no_entities') : $t('statistics.totalValues')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="entityOptions.length < 2"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="clear" slot-scope="props" v-if="entityOptions.length > 1 && filters.entity !== null">
                <div id="entities-multiselect-clear" class="multiselect__clear" @mousedown.prevent.stop="filters.entity = null"></div>
              </template>
            </multiselect>
          </span>

          <span v-else>
            <label class="form-label">{{$t('statistics.select_cluster')}}:</label>
            <multiselect
              id="clusters-multiselect"
              v-model="filters.cluster"
              :options="clusterOptions"
              track-by="id"
              :custom-label="formatedEntityLabel"
              :placeholder="!clusterOptions.length ? $t('statistics.no_clusters') : $t('statistics.totalValues')"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
              :disabled="clusterOptions.length < 2"
            >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="clear" slot-scope="props" v-if="clusterOptions.length > 1 && filters.cluster !== null">
                <div id="clusters-multiselect-clear" class="multiselect__clear" @mousedown.prevent.stop="filters.cluster = null"></div>
              </template>
            </multiselect>
          </span>
        </div>

        <div class="col-12 col-sm-3">
          <label class="form-label">{{$t('statistics.date_field')}}:</label>
          <multiselect
            v-model="filters.dateType"
            :options="dateOptions"
            select-label=""
            deselect-label="×"
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :searchable="true"
            :disabled="isLoading"
          >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
            <template slot="singleLabel" slot-scope="props">
              <span class="option__name">{{ $t(`statistics.${props.option}` )}}</span>
            </template>
            <template slot="option" slot-scope="props">
              <span class="option__name">{{ $t(`statistics.${props.option}`) }}</span>
            </template>
          </multiselect>
        </div>

        <div class="col-12 col-sm-4">
          <RangeDatePicker
            id="range-date-picker"
            :label="`${$t('statistics.date_interval')}:`"
            v-model="filters.dateRange"
            placeholder="---"
            :clearable="false"
            :disabled="isLoading"
          >
          </RangeDatePicker>
        </div>
      </div>

      <div v-if="!filters.showEntityStats && filters.cluster && expandCluster" class="row mb-5">
        <div class="col-12">
          <va-badge outline v-for="(name, index) in filters.cluster.entity_names" :key="index">
            {{name}}
          </va-badge>
        </div>
      </div>
    </form>

    <div v-show="isLoading" class="no-data-wrapper">
      <h6><b>{{$t('tables.loading')}}</b></h6>
      <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
    </div>

    <div v-show="!isLoading && (!hasData || totalValues === null)" class="no-data-wrapper">
      <span class="is-bold">{{$t('pyramid.no_data')}}</span>
    </div>

    <div v-show="!isLoading && hasData">
      <div class="section">
        <h5 class="section-title">{{ $t('statistics.registos') }}</h5>
        <div v-show="hasData" id="chartdiv" ref="chartdiv"></div>
      </div>
    </div>

  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import Multiselect from 'vue-multiselect'
import * as am4core from '@amcharts/amcharts4/core'
import { StatisticsService } from '@/api/LeadsService'
import * as am4charts from '@amcharts/amcharts4/charts'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import { SANKEY_STATS } from '@/local-storage/LocalStorageKeys'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import LoadingAnimation from '@/components/users/components/Helpers/LoadingAnimation'
import am4themes_animated from '@amcharts/amcharts4/themes/animated' // eslint-disable-line
import am4themes_spiritedaway from '@amcharts/amcharts4/themes/spiritedaway' // eslint-disable-line
// import am4themes_moonrisekingdom from '@amcharts/amcharts4/themes/moonrisekingdom' // eslint-disable-line

am4core.useTheme(am4themes_animated)
am4core.useTheme(am4themes_spiritedaway)

export default {
  components: {
    Multiselect,
    Breadcrumbs,
    RangeDatePicker,
    LoadingAnimation
  },
  mixins: [
    MultiselectLabelFormatter
  ],
  data () {
    return {
      expandCluster: false,
      chart: null,
      isLoading: false,
      dataset: {},
      totalValues: null,
      filters: {
        showEntityStats: true,
        entity: null,
        cluster: null,
        dateRange: {
          start: this.moment().subtract(1, 'months').format('YYYY-MM-DD'),
          end: this.moment().format('YYYY-MM-DD')
        },
        dateType: 'created_at'
      },
      dateOptions: [
        'created_at',
        'updated_at',
        'qualified_at'
      ]
    }
  },
  methods: {
    createChart: function () {
      let chart = am4core.create('chartdiv', am4charts.SankeyDiagram)
      chart.hiddenState.properties.opacity = 0
      chart.data = this.chartData
      chart.dataFields.fromName = 'from'
      chart.dataFields.toName = 'to'
      chart.dataFields.value = 'value'
      chart.links.template.colorMode = 'gradient'
      chart.paddingTop = 5
      chart.paddingRight = 150
      chart.paddingBottom = 30
      chart.nodes.template.nameLabel.label.truncate = false
      // chart.nodes.template.nameLabel.label.wrap = true
      chart.exporting.menu = new am4core.ExportMenu()
      chart.exporting.filePrefix = 'LeadsEvollu'
      chart.exporting.dataFields = { from: 'De', to: 'Para', value: 'Leads' }

      chart.nodes.template.nameLabel.label.adapter.add('text', function (text, target, o) {
        let node = target.parent.parent
        // if (node.outgoingDataItems.length > 0) console.log(node.outgoingDataItems.values[0].dataContext)
        if (node.level === 0 && node.outgoingDataItems.length > 0) {
          return text + ' (' + node.outgoingDataItems.values[0].dataContext.fromTotal + ')'
        } else if (node.level !== 0 && node.incomingDataItems.length > 0) {
          return text + ' (' + node.incomingDataItems.values[0].dataContext.toTotal + ')'
        }
        return text
      })

      chart.exporting.menu.items = [{
        'label': '...',
        'menu': [
          { 'type': 'png', 'label': 'PNG' },
          { 'type': 'xlsx', 'label': 'XLSX' },
          { 'label': 'Print', 'type': 'print' }
        ]
      }]

      this.chart = chart
    },
    loadBaseData () {
      this.isLoading = true
      StatisticsService.getFullStats(this.requestParams)
        .then(originalData => this.createDataset(originalData))
        .catch((error) => {
          console.log(error)
          this.showToast(this.$t('statistics.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    createDataset: function (data) {
      this.dataset = {}
      this.totalValues = null

      if (data.length) {
        data
          .sort((a, b) => a.id - b.id)
          .forEach(element => {
            this.dataset[element.id] = this.createDatasetEntry(element)
          })

        this.totalValues = this.calculateAggregateData(Object.keys(this.dataset))
      }
    },
    calculateAggregateData: function (entityIds) {
      let aggregate = this.createDatasetEntry()
      const keys = Object.keys(aggregate)

      entityIds.forEach(entityId => {
        if (this.dataset.hasOwnProperty(entityId)) {
          keys.forEach(key => {
            aggregate[key] += this.dataset[entityId][key]
          })
        }
      })

      return aggregate
    },
    createDatasetEntry: function (data = {}) {
      return {
        counter: 0,
        counter_loss: 0,
        kiosk: 0,
        app: 0,
        app_loss: 0,
        web: 0,
        web_loss: 0,
        kiosk_ht: 0,
        kiosk_ht_loss: 0,
        questionnaires: 0,
        contacts_no_success: 0,
        contacts_no_success_hl: 0,
        contacts_success: 0,
        contacts_success_hl: 0,
        contacts_postponed: 0,
        contacts_postponed_hl: 0,
        requested_appointment: 0,
        requested_appointment_hl: 0,
        converted_to_appointment: 0,
        converted_to_appointment_hl: 0,
        appointment_not_applicable: 0,
        appointment_not_applicable_hl: 0,
        appointment_no_sale: 0,
        appointment_no_sale_hl: 0,
        appointment_possible_sale: 0,
        appointment_possible_sale_hl: 0,
        trial: 0,
        trial_hl: 0,
        qualified: 0,
        appointment_missed: 0,
        appointment_missed_hl: 0,
        appointment_ent: 0,
        appointment_ent_hl: 0,
        sale: 0,
        sale_hl: 0,
        cancelled: 0,
        cancelled_ht_loss: 0,
        cancelled_ht_no_loss: 0,
        sessions: 0,
        ...data
      }
    },
    getFullEntityById: function (id) {
      return this.fullEntities.find(e => e.id === id)
    },
    loadLocalStorage: function () {
      return new Promise((resolve, reject) => {
        const state = LocalStorageHelper.get(SANKEY_STATS)
        if (state !== null) {
          this.filters = {
            ...this.fitlers,
            ...state
          }
        }
        resolve()
      })
    }
  },
  computed: {
    ...mapGetters([
      'clusters'
    ]),
    audiologyService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollead_server)
    },
    fullEntities: function () {
      return this.audiologyService ? this.audiologyService.entities : []
    },
    requestParams: function () {
      return {
        from: this.filters.dateRange.start,
        until: this.filters.dateRange.end,
        date: this.filters.dateType
      }
    },
    entityOptions: function () {
      return Object.values(this.dataset)
        .map(element => {
          let entity = this.getFullEntityById(element.id)
          return {
            id: element.id,
            name: element.name,
            cities: entity ? entity.cities : []
          }
        })
    },
    clusterOptions: function () {
      return this.clusters
        .map(cluster => {
          let entitiesWithPermissions = this.entityOptions.filter(entity => cluster.entitiesIds.some(entityId => entity.id === entityId))
          cluster.entitiesWithPermissions = entitiesWithPermissions.length
          cluster.entity_names = entitiesWithPermissions.map(e => e.name)
          return cluster
        })
        .filter(cluster => cluster.entitiesWithPermissions > 0)
    },
    stats: function () {
      if (this.filters.showEntityStats && this.filters.entity) {
        return this.dataset[this.filters.entity.id]
      } else if (!this.filters.showEntityStats && this.filters.cluster) {
        return this.calculateAggregateData(this.filters.cluster.entitiesIds)
      } else {
        return this.totalValues
      }
    },
    chartData: function () {
      return [
        {
          'from': this.$t('sankey.tests_with_loss'),
          'to': this.$t('sankey.not_submitted_with_loss'),
          'value': this.cancelledHtLoss,
          'fromTotal': (this.cancelledHtLoss + this.htLoss),
          'toTotal': this.cancelledHtLoss
        },
        {
          'from': this.$t('sankey.tests_with_loss'),
          'to': this.$t('sankey.submitted_with_loss'),
          'value': this.htLoss,
          'fromTotal': (this.cancelledHtLoss + this.htLoss),
          'toTotal': this.submittedLoss
        },
        {
          'from': this.$t('sankey.counter'),
          'to': this.$t('sankey.submitted_with_loss'),
          'value': this.counterEntriesLoss,
          'fromTotal': this.counterEntries,
          'toTotal': this.submittedLoss
        },
        {
          'from': this.$t('sankey.counter'),
          'to': this.$t('sankey.submitted_without_loss'),
          'value': this.counterEntriesNoLoss,
          'fromTotal': this.counterEntries,
          'toTotal': this.submittedNoLoss
        },
        {
          'from': this.$t('sankey.app'),
          'to': this.$t('sankey.submitted_with_loss'),
          'value': this.appEntriesLoss,
          'fromTotal': this.appEntries,
          'toTotal': this.submittedLoss
        },
        {
          'from': this.$t('sankey.app'),
          'to': this.$t('sankey.submitted_without_loss'),
          'value': this.appEntriesNoLoss,
          'fromTotal': this.appEntries,
          'toTotal': this.submittedNoLoss
        },
        {
          'from': this.$t('sankey.web'),
          'to': this.$t('sankey.submitted_with_loss'),
          'value': this.webEntriesLoss,
          'fromTotal': this.webEntries,
          'toTotal': this.submittedLoss
        },
        {
          'from': this.$t('sankey.web'),
          'to': this.$t('sankey.submitted_without_loss'),
          'value': this.webEntriesNoLoss,
          'fromTotal': this.webEntries,
          'toTotal': this.submittedNoLoss
        },
        {
          'from': this.$t('sankey.contact_requests'),
          'to': this.$t('sankey.submitted_without_loss'),
          'value': this.kioskNoHt,
          'fromTotal': this.kioskNoHt,
          'toTotal': this.submittedNoLoss
        },
        { 'from': this.$t('sankey.tests_without_loss'),
          'to': this.$t('sankey.submitted_without_loss'),
          'value': this.htNoLoss,
          'fromTotal': (this.cancelledHtNoLoss + this.htNoLoss),
          'toTotal': this.submittedNoLoss
        },
        {
          'from': this.$t('sankey.tests_without_loss'),
          'to': this.$t('sankey.not_submitted_without_loss'),
          'value': this.cancelledHtNoLoss,
          'fromTotal': (this.cancelledHtNoLoss + this.htNoLoss),
          'toTotal': this.cancelledHtNoLoss
        },
        {
          'from': this.$t('sankey.submitted_with_loss'),
          'to': this.$t('sankey.unreachable'),
          'value': this.contactsNoSuccessHLCount,
          'fromTotal': this.htLoss,
          'toTotal': this.contactsNoSuccessCount
        },
        {
          'from': this.$t('sankey.submitted_without_loss'),
          'to': this.$t('sankey.unreachable'),
          'value': this.contactsNoSuccessCount - this.contactsNoSuccessHLCount,
          'fromTotal': (this.counterEntries + this.kioskNoHt + this.htNoLoss),
          'toTotal': this.contactsNoSuccessCount
        },
        {
          'from': this.$t('sankey.submitted_with_loss'),
          'to': this.$t('sankey.contact_postponed'),
          'value': this.contactsPostponedHLCount,
          'fromTotal': this.htLoss,
          'toTotal': this.contactsPostponedCount
        },
        {
          'from': this.$t('sankey.submitted_without_loss'),
          'to': this.$t('sankey.contact_postponed'),
          'value': this.contactsPostponedCount - this.contactsPostponedHLCount,
          'fromTotal': (this.counterEntries + this.kioskNoHt + this.htNoLoss),
          'toTotal': this.contactsPostponedCount
        },
        {
          'from': this.$t('sankey.submitted_with_loss'),
          'to': this.$t('sankey.with_appointment'),
          'value': this.scheduledAppointmentsHL,
          'fromTotal': this.htLoss,
          'toTotal': this.scheduledAppointments
        },
        {
          'from': this.$t('sankey.submitted_without_loss'),
          'to': this.$t('sankey.with_appointment'),
          'value': this.scheduledAppointments - this.scheduledAppointmentsHL,
          'fromTotal': (this.counterEntries + this.kioskNoHt + this.htNoLoss),
          'toTotal': this.scheduledAppointments
        },
        {
          'from': this.$t('sankey.submitted_with_loss'),
          'to': this.$t('sankey.contact_success'),
          'value': this.contactsSuccessHLCount,
          'fromTotal': this.htLoss,
          'toTotal': this.contactsSuccessCount
        },
        {
          'from': this.$t('sankey.submitted_without_loss'),
          'to': this.$t('sankey.contact_success'),
          'value': this.contactsSuccessCount - this.contactsSuccessHLCount,
          'fromTotal': (this.counterEntries + this.kioskNoHt + this.htNoLoss),
          'toTotal': this.contactsSuccessCount
        },
        {
          'from': this.$t('sankey.with_appointment'),
          'to': this.$t('sankey.appointment_booked'),
          'value': this.convertedToAppointmentCount,
          'fromTotal': this.scheduledAppointments,
          'toTotal': this.convertedToAppointmentCount
        },
        {
          'from': this.$t('sankey.with_appointment'),
          'to': this.$t('sankey.appointment_success'),
          'value': this.totalAppointments,
          'fromTotal': this.scheduledAppointments,
          'toTotal': this.totalAppointments
        },
        {
          'from': this.$t('sankey.with_appointment'),
          'to': this.$t('sankey.missed_appointment'),
          'value': this.appointmentMissedCount,
          'fromTotal': this.scheduledAppointments,
          'toTotal': this.appointmentMissedCount
        },
        {
          'from': this.$t('sankey.appointment_success'),
          'to': this.$t('sankey.normal_hearing'),
          'value': this.appointmentNotApplicableCount,
          'fromTotal': this.totalAppointments,
          'toTotal': this.appointmentNotApplicableCount
        },
        {
          'from': this.$t('sankey.appointment_success'),
          'to': this.$t('sankey.ref_ent'),
          'value': this.appointmentEntCount,
          'fromTotal': this.totalAppointments,
          'toTotal': this.appointmentEntCount
        },

        {
          'from': this.$t('sankey.appointment_success'),
          'to': this.$t('sankey.appointment_no_sale'),
          'value': this.appointmentNoSaleCount,
          'fromTotal': this.totalAppointments,
          'toTotal': this.appointmentNoSaleCount
        },
        {
          'from': this.$t('sankey.appointment_success'),
          'to': this.$t('sankey.appointment_possible_sale'),
          'value': this.appointmentPossibleSaleCount,
          'fromTotal': this.totalAppointments,
          'toTotal': this.appointmentPossibleSaleCount
        },
        {
          'from': this.$t('sankey.appointment_success'),
          'to': this.$t('sankey.appointment_trial'),
          'value': this.trialCount,
          'fromTotal': this.totalAppointments,
          'toTotal': this.trialCount
        },
        {
          'from': this.$t('sankey.appointment_success'),
          'to': this.$t('sankey.appointment_sale'),
          'value': this.buysCount,
          'fromTotal': this.totalAppointments,
          'toTotal': this.buysCount
        }
      ]
    },
    counterEntries: function () {
      return this.stats ? this.stats.counter : 0
    },
    counterEntriesLoss: function () {
      return this.stats ? this.stats.counter_loss : 0
    },
    counterEntriesNoLoss: function () {
      return this.counterEntries - this.counterEntriesLoss
    },
    kioskEntries: function () {
      return this.stats ? this.stats.kiosk : 0
    },
    appEntries: function () {
      return this.stats ? this.stats.app : 0
    },
    appEntriesNoLoss: function () {
      return this.appEntries - this.appEntriesLoss
    },
    appEntriesLoss: function () {
      return this.stats ? this.stats.app_loss : 0
    },
    webEntries: function () {
      return this.stats ? this.stats.web : 0
    },
    webEntriesLoss: function () {
      return this.stats ? this.stats.web_loss : 0
    },
    webEntriesNoLoss: function () {
      return this.webEntries - this.webEntriesLoss
    },
    kioskNoHt: function () {
      return this.stats ? this.stats.kiosk - this.stats.kiosk_ht : 0
    },
    ht: function () {
      return this.stats ? this.stats.kiosk_ht : 0
    },
    htLoss: function () {
      return this.stats ? this.stats.kiosk_ht_loss : 0
    },
    htNoLoss: function () {
      return this.stats ? this.stats.kiosk_ht - this.stats.kiosk_ht_loss : 0
    },
    submittedLoss: function () {
      return this.counterEntriesLoss + this.appEntriesLoss + this.webEntriesLoss + this.htLoss
    },
    submittedNoLoss: function () {
      return this.counterEntriesNoLoss + this.appEntriesNoLoss + this.webEntriesNoLoss + this.kioskNoHt + this.htNoLoss
    },
    contactsCount: function () {
      return this.stats ? this.stats.contacts_no_success + this.stats.contacts_success + this.stats.contacts_postponed : 0
    },
    contactsSuccessCount: function () {
      return this.stats ? this.stats.contacts_success : 0
    },
    contactsSuccessHLCount: function () {
      return this.stats ? this.stats.contacts_success_hl : 0
    },
    totalSuccessContacts: function () {
      return this.contactsSuccessCount + this.scheduledAppointments
    },
    contactsNoSuccessCount: function () {
      return this.stats ? this.stats.contacts_no_success : 0
    },
    contactsNoSuccessHLCount: function () {
      return this.stats ? this.stats.contacts_no_success_hl : 0
    },
    contactsPostponedCount: function () {
      return this.stats ? this.stats.contacts_postponed : 0
    },
    contactsPostponedHLCount: function () {
      return this.stats ? this.stats.contacts_postponed_hl : 0
    },
    convertedToAppointmentCount: function () {
      return this.stats ? this.stats.converted_to_appointment : 0
    },
    convertedToAppointmentHLCount: function () {
      return this.stats ? this.stats.converted_to_appointment_hl : 0
    },
    scheduledAppointments: function () {
      return this.convertedToAppointmentCount + this.totalAppointments + this.appointmentMissedCount
    },
    scheduledAppointmentsHL: function () {
      return this.convertedToAppointmentHLCount + this.totalAppointmentsHL + this.appointmentMissedHLCount
    },
    appointmentNotApplicableCount: function () {
      return this.stats ? this.stats.appointment_not_applicable : 0
    },
    appointmentNotApplicableHLCount: function () {
      return this.stats ? this.stats.appointment_not_applicable_hl : 0
    },
    appointmentNoSaleCount: function () {
      return this.stats ? this.stats.appointment_no_sale : 0
    },
    appointmentNoSaleHLCount: function () {
      return this.stats ? this.stats.appointment_no_sale_hl : 0
    },
    appointmentPossibleSaleCount: function () {
      return this.stats ? this.stats.appointment_possible_sale : 0
    },
    appointmentPossibleSaleHLCount: function () {
      return this.stats ? this.stats.appointment_possible_sale_hl : 0
    },
    appointmentMissedCount: function () {
      return this.stats ? this.stats.appointment_missed : 0
    },
    appointmentMissedHLCount: function () {
      return this.stats ? this.stats.appointment_missed_hl : 0
    },
    appointmentEntCount: function () {
      return this.stats ? this.stats.appointment_ent : 0
    },
    appointmentEntHLCount: function () {
      return this.stats ? this.stats.appointment_ent_hl : 0
    },
    totalAppointmentsHL: function () {
      return this.appointmentPossibleSaleHLCount +
            this.appointmentEntHLCount +
            this.appointmentNotApplicableHLCount +
            this.appointmentNoSaleHLCount +
            this.trialHLCount + this.buysHLCount
    },
    totalAppointments: function () {
      return this.appointmentPossibleSaleCount +
            this.appointmentEntCount +
            this.appointmentNotApplicableCount +
            this.appointmentNoSaleCount +
            this.trialCount + this.buysCount
    },
    trialCount: function () {
      return this.stats ? this.stats.trial : 0
    },
    trialHLCount: function () {
      return this.stats ? this.stats.trial_hl : 0
    },
    buysCount: function () {
      return this.stats ? this.stats.sale : 0
    },
    buysHLCount: function () {
      return this.stats ? this.stats.sale_hl : 0
    },
    qualifiedCount: function () {
      return this.stats ? this.stats.qualified : 0
    },
    cancelledCount: function () {
      return this.stats ? this.stats.cancelled : 0
    },
    cancelledHtLoss: function () {
      return this.stats ? this.stats.cancelled_ht_loss : 0
    },
    cancelledHtNoLoss: function () {
      return this.stats ? this.stats.cancelled_ht_no_loss : 0
    },
    sessionEntries: function () {
      return this.stats ? this.stats.sessions : 0
    },
    consultationsWithLoss: function () {
      return this.qualifiedCount + this.appointmentEntCount
    },
    hasData: function () {
      return this.cancelledCount > 0 || this.kioskEntries > 0 || this.counterEntries > 0 || this.appEntries > 0 || this.webEntries > 0
    }
  },
  watch: {
    entityOptions: {
      handler: function (options) {
        if (options.length === 1) {
          this.filters.entity = options[0]
        } else if (this.filters.entity && !options.some(option => option.id === this.filters.entity.id)) {
          this.filters.entity = null
        }
      },
      immediate: true
    },
    clusterOptions: {
      handler: function (options) {
        // Multiselect did not update label unless v-model was reset to null and then set again
        const previousValue = this.filters.cluster
        this.filters.cluster = null
        let newSelectedValue = null

        if (options.length === 1) {
          newSelectedValue = options[0]
        } else if (previousValue) {
          newSelectedValue = options.find(option => option.id === previousValue.id) || null
        }

        this.$nextTick(() => {
          this.filters.cluster = newSelectedValue
        })
      },
      immediate: true
    },
    filters: {
      handler: function () {
        LocalStorageHelper.set(SANKEY_STATS, this.filters)
      },
      deep: true
    },
    'filters.showEntityStats': function (value) {
      if (!value && !this.clusters.length) {
        this.filters.showEntityStats = true
      }
    },
    requestParams: function (value) {
      this.loadBaseData()
    },
    chartData: function (value) {
      if (this.chart) {
        this.chart.data = value
      }
    }
  },
  mounted () {
    this.createChart()
    this.loadLocalStorage()
      .then(() => {
        this.loadBaseData()
      })
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.dispose()
    }
  }
}
</script>

<style lang="scss">
.sankey-diagram {
  #chartdiv {
    width: 100%;
    height: 750px;
  }
}
</style>
